import FormFieldValues from "@/components/Form/Fields/FormFieldValues";
import SuccessAnimation from "@/components/LottieAnimations/Success/SuccessAnimation";
import { AssetTypes } from "@/model/AssetTypes";
import useOpenResubmissionForm from "@/modules/resubmission/hooks/useOpenResubmissionForm";
import UnitStruct from "@/redux/actions/struct/implemented/UnitStruct";
import { store } from "@/redux/store";
import arrayMutators from "final-form-arrays";
import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { Form, FormSpy } from "react-final-form";
import { useDispatch } from "react-redux";
import { checkListViewSplitView } from "../../../../../components/ListViewPDF/OldListViewPDF";
import DeleteAnimation from "../../../../../components/LottieAnimations/Delete/DeleteAnimation";
import DocumentScanAnimation from "../../../../../components/LottieAnimations/DocumentScan/DocumentScanAnimation";
import MetaRow from "../../../../../components/MetaRow/MetaRow";
import ModalManager from "../../../../../components/ModalComponent/ModalManager";
import { PDFHighlight } from "../../../../../components/PDFViewer/PDFViewer";
import DebugDataComponent from "../../../../../debug/DebugDataComponent";
import i18n from "../../../../../i18n";
import BFButton from "../../../../../modules/abstract-ui/general/Button/BFButton";
import BFMessage from "../../../../../modules/abstract-ui/general/Message/BFMessage";
import { GFPromptBlock } from "../../../../../modules/generic-forms-impl/layout-components/LayoutComponentsImpl";
import { patchAssetData } from "../../../../../redux/actions/application/application-actions";
import { useConstants, useViewportSelector } from "../../../../../redux/hooks";
import DataBus from "../../../../../services/DataBus";
import { DataBusSubKeys } from "../../../../../utils/Constants";
import InvoiceService from "../../InvoiceService";
import { Invoice } from "../../RAInterfaces";
import { RA_UPLOAD_INCOMING_INVOICE_LIST } from "../../RAUtils";
import RAInvoiceForm from "../InvoiceForm/RAInvoiceForm";
import { InvoiceBaseFormModel } from "../InvoiceForm/RAInvoiceFormModel";
import RADuplicateCheck from "./RADuplicateCheck";
import "./RAUploadInvoiceEntry.scss";

interface Props {
  asset: Invoice;
  selected: boolean;
  onSelect: (force?: boolean) => void;
  onClearSelection: () => void;
  onFocus: (highlight?: PDFHighlight) => void;
  onOpenDrawer: (initial: "history" | "comments") => void;
  activeStatus: string[];
  pinned: boolean;
  setPinned: (pinned: boolean) => void;
  reloadList: () => void;
}
const RAUploadInvoiceEntry = (props: Props) => {
  const ref = useRef<HTMLDivElement>();

  const disableEdit =
    props.asset?._deleted ||
    props.activeStatus.indexOf(props.asset?.data.status) === -1;

  useEffect(() => {
    if (props.selected) {
      props.onSelect(true);
    }
  }, [props.asset]);

  return (
    <div
      ref={ref}
      className={`ra-upload-invoice-entry`}
      onClick={(ev) => {
        if (!(ev.target as Element).classList.contains("delete-button")) {
          if (!disableEdit) {
            props.onSelect();
          }
        }
      }}
    >
      {(props.asset?.data.invoice.extractStatus === "init" ||
        props.asset?.data.invoice.extractStatus === "loading") && (
        <ExtractLoading {...props} />
      )}
      {(props.asset.data.invoice.extractStatus === "finish" ||
        props.asset.data.invoice.extractStatus === "abort" ||
        props.asset.data.invoice.extractStatus === "fail") && (
        <>
          <ViewInvoiceEntry
            activeStatus={props.activeStatus}
            asset={props.asset}
            selected={props.selected}
            onFocus={props.onFocus}
            onSelect={props.onSelect}
            onClearSelection={props.onClearSelection}
            pinned={props.pinned}
            setPinned={props.setPinned}
            onOpenDrawer={props.onOpenDrawer}
            reloadList={props.reloadList}
          />
        </>
      )}

      {props.selected && <div className="transition-to-pdf"></div>}

      {props.asset?._deleted && (
        <div className="overlay">
          <div className="overlay-content">
            <DeleteAnimation style={{ width: 80, height: 80 }} />
            {/* <BfIcon data="bin-2" type="light" size="sm" /> Rechnung gelöscht */}
          </div>
        </div>
      )}
      {props.asset?.data.status !== "init" && (
        <div className="overlay">
          <div className="overlay-content">
            <SuccessAnimation style={{ width: 100, height: 100 }} />
            {/* <BfIcon
              data="singe-netrual-actions-process"
              type="light"
              size="sm"
            />{" "}
            In Genehmigung */}
          </div>
        </div>
      )}
      <div className="meta-container">
        <MetaRow
          meta={props.asset?.meta}
          pretext={i18n.t(
            "ra:UploadIncomingInvoice.metaPreText",
            "Hochgeladen von"
          )}
        />
      </div>
    </div>
  );
};
const ExtractLoading = (props: Props) => {
  const dispatch = useDispatch();
  const [aborting, setAborting] = useState(false);
  const cdnFile = props.asset?.data.invoice.file;

  const abort = () => {
    setAborting(true);
    InvoiceService.abortExtraction(props.asset)
      .then((result) => {
        dispatch(patchAssetData(result._id, result));
      })
      .finally(() => {
        setAborting(false);
      });
  };
  return (
    <div className="extract-loader">
      <DocumentScanAnimation style={{ width: 140, height: 140 }} />
      <div className="text">
        <div className="file">{cdnFile.filename}</div>
        <div className="status">
          {i18n.t(
            "ra:UploadIncomingInvoice.extractDataFromDocument",
            "Extrahiert Daten aus dem Dokument"
          )}
        </div>
        <div className="action">
          <BFButton
            appearance="link"
            onClick={() => abort()}
            loading={aborting}
          >
            {i18n.t("ra:UploadIncomingInvoice.manualEdit", "Manuell ausfüllen")}
          </BFButton>
        </div>
        {/* <Loader size="md" content="Extrahiert Daten aus dem Dokument" /> */}
      </div>
    </div>
  );
};

export default React.memo(RAUploadInvoiceEntry, (prev, next) => {
  // fix memo issue with functions - we should consider some general solution for this issue
  return _.isEqualWith(prev, next, (a, b) => {
    if (typeof a === "function" && typeof b === "function") {
      return true;
    }
    return undefined;
  });
});

export const sendInvoiceToApproval = async (
  asset: Invoice,
  values: InvoiceBaseFormModel
) => {
  try {
    const result = await InvoiceService.startApprovalForInvoice(
      asset._id,
      values,
      false
    );
    store.dispatch(patchAssetData(result._id, result));
    DataBus.emit(DataBusSubKeys.SELECT_NEXT, {
      tableIdentifier: RA_UPLOAD_INCOMING_INVOICE_LIST,
      smooth: true,
    });
  } catch (err) {
    console.error("Error sending invoice to approval", err);
    if (err?.response?.status === 300) {
      const responses = err.response.data as Invoice[];
      ModalManager.confirm({
        title: i18n.t(
          "ra:UploadIncomingInvoice.DuplicateModalTitle",
          "Mögliches Duplikat dieser Rechnung"
        ),
        size: "xxl",
        message: <RADuplicateCheck original={asset} duplicates={responses} />,
        confirmButtonText: i18n.t(
          "ra:UploadIncomingInvoice.DuplicateConfirmButton",
          "Trotzdem in Genehmigung schicken"
        ),
        onConfirm: async () => {
          const result = await InvoiceService.startApprovalForInvoice(
            asset._id,
            values,
            true
          );
          store.dispatch(patchAssetData(result._id, result));
          DataBus.emit(DataBusSubKeys.SELECT_NEXT, {
            tableIdentifier: RA_UPLOAD_INCOMING_INVOICE_LIST,
            smooth: true,
          });
        },
      });
    } else {
      DataBus.emit(DataBusSubKeys.TOAST, {
        type: "error",
        text: i18n.t(
          "ra:UploadIncomingInvoice.ErrorSavingInvoice",
          "Fehler beim Speichern der Rechnung"
        ),
      });
    }
  }
};

interface ViewInvoiceEntryProps {
  asset: Invoice;
  selected: boolean;
  onSelect: (force?: boolean) => void;
  onFocus: (highlight?: PDFHighlight) => void;
  onClearSelection: () => void;
  pinned: boolean;
  setPinned: (pinned: boolean) => void;
  activeStatus: string[];
  onOpenDrawer: (initial: "history" | "comments") => void;
  reloadList: () => void;
}
const ViewInvoiceEntry = (props: ViewInvoiceEntryProps) => {
  const viewport = useViewportSelector();

  const [visited, setVisited] = useState(false);
  useEffect(() => {
    if (props.selected) {
      setVisited(true);
    }
  }, [props.selected]);

  useEffect(() => {
    if (
      props.selected &&
      props.activeStatus.indexOf(props.asset?.data.status) === -1
    ) {
      props.onClearSelection();
    }
  }, [props.asset, props.activeStatus]);

  useEffect(() => {
    if (props.selected) {
      props.onSelect(true);
    }
  }, [props.asset]);

  const splitView = checkListViewSplitView(viewport);

  return (
    <div className="view-invoice-entry">
      <div className="invoice-entry-header">
        <div className="id">
          {props.asset?.id} - {UnitStruct.getUnitLabel(props.asset?.data.type)}
        </div>
        <div className="fill" />
        <div className="fill" />
        <div className="actions">
          <BFButton
            tooltip={{
              tooltip: i18n.t("ra:Actions.Comments", "Kommentare"),
            }}
            appearance={"clear-on-white"}
            icon={{
              type: "light",
              data: "conversation-chat-1",
              size: "xs",
            }}
            badge={{
              content: props.asset.communication?.numComments || undefined,
              color: "blue",
            }}
            onClick={() => props.onOpenDrawer("comments")}
          />
          <BFButton
            tooltip={{
              tooltip: i18n.t("ra:Actions.History", "Historie"),
            }}
            appearance={"clear-on-white"}
            icon={{
              type: "light",
              data: "synchronize-arrow-clock",
              size: "xs",
            }}
            onClick={() => props.onOpenDrawer("history")}
          />
          {!splitView && (
            <>
              <div className="divider" />
              <BFButton
                tooltip={{
                  tooltip: i18n.t("ra:Actions.ShowPDF", "PDF anzeigen"),
                }}
                appearance={"clear-on-white"}
                icon={{ type: "light", data: "file-pdf", size: "xs" }}
                onClick={() => DataBus.emit(DataBusSubKeys.OPEN_PDF_VIEWER, {})}
              />
            </>
          )}
        </div>
      </div>
      {/* TODO_INVOICE */}
      {/* {props.asset?.data.correction?.isCorrection && (
        <div className="correction">
          <BFMessage
            type="info"
            text={
              <>
                <div className={`text`}>
                  {i18n.t(
                    "ra:UploadIncomingInvoice.InvoiceIsCorrection",
                    "Diese Rechnung ist eine Korrektur-Rechnung."
                  )}
                </div>
                <BFButton
                  appearance="link"
                  size="xs"
                  onClick={() =>
                    DataBusDefaults.route({
                      route: `__details/${props.asset?.data.correction.linkedInvoice}`,
                      append: true,
                    })
                  }
                >
                  {i18n.t(
                    "ra:UploadIncomingInvoice.ShowOriginal",
                    "Original anzeigen"
                  )}
                </BFButton>
              </>
            }
          ></BFMessage>
        </div>
      )} */}
      <UploadInvoiceForm
        asset={props.asset}
        onFocus={props.onFocus}
        reloadList={props.reloadList}
      />
      {props.asset.data.invoice.extractStatus === "fail" && (
        <div className="failed-extract-info">
          <BFMessage
            showIcon
            type="warning"
            title={i18n.t(
              "ra:UploadIncomingInvoice.ExtractionFailedTitle",
              "Extrahierung fehlgeschlagen"
            )}
            text={i18n.t(
              "ra:UploadIncomingInvoice.ExtractonFailedDescription",
              "Bei der Extrahierung ist ein Fehler aufgetreten, bitte füllen Sie die Daten manuell aus"
            )}
            closable
          />
        </div>
      )}
      {/* {validation.costCenterSum ? (
        <div className={`__error margin-top-10 margin-bottom-10`}>
          {validation.costCenterSum}
        </div>
      ) : null} */}
    </div>
  );
};

export const UploadInvoiceForm = (props: {
  asset: Invoice;
  onFocus: (highlight?: PDFHighlight) => void;
  onClearSelection?: () => void;
  reloadList: () => void;
}) => {
  const { openResubmissionBtnIconProps, openResubmissionDialog } =
    useOpenResubmissionForm();

  const [initialState] = useState(
    InvoiceService.getInitialDataForInvoice(props.asset)
  );
  const dispatch = useDispatch();
  const constants = useConstants();
  const [deleting, setDeleting] = useState(false);

  const activeStatus = ["init"];
  const disableEdit =
    props.asset._deleted ||
    activeStatus.indexOf(props.asset.data.status) === -1;

  return (
    <Form
      mutators={{
        ...arrayMutators,
        setValue: ([field, value], state, { changeValue }) => {
          if (state.formState.values) {
            changeValue(state, field, () => value);
          }
        },
      }}
      initialValues={initialState}
      onSubmit={async (values) => {
        await sendInvoiceToApproval(props.asset, values);
      }}
      render={({ handleSubmit, form, submitting, pristine, values }) => (
        <div className={`form ra-upload-invoice-form`}>
          <GFPromptBlock />
          <RAInvoiceForm
            ignoreCompare
            form={form}
            uploadForm
            invoice={props.asset}
            onHighlight={props.onFocus}
          />

          <FormSpy
            subscription={{ values: true, errors: true }}
            render={({ values, errors }) => (
              <DebugDataComponent
                data={{ value: values, error: errors, initialState }}
              />
            )}
          />

          <div className="__flex padding-top-10">
            <BFButton
              className="delete-button"
              disabled={disableEdit}
              loading={deleting}
              appearance="outline-red"
              onClick={() => {
                ModalManager.confirm({
                  message: i18n.t(
                    "ra:UploadIncomingInvoice.deleteInvoiceText",
                    "Wollen Sie die Eingangsrechnung wirklich löschen?"
                  ),
                  title: i18n.t(
                    "ra:UploadIncomingInvoice.deleteInvoiceTitle",
                    "Eingangsrechnung löschen"
                  ),
                  onConfirm: () => {
                    props.onClearSelection?.();
                    setDeleting(true);
                    InvoiceService.deleteInvoice(props.asset)
                      .catch((err) => {
                        DataBus.emit(DataBusSubKeys.TOAST, {
                          type: "error",
                          text: i18n.t(
                            "ra:UploadIncomingInvoice.errorDeleteInvoice",
                            "Fehler beim Löschen der Rechnung"
                          ),
                        });
                      })
                      .finally(() => setDeleting(false));
                  },
                  confirmButtonText: i18n.t(
                    "ra:UploadIncomingInvoice.deleteInvoiceConfirmButton",
                    "Löschen"
                  ),
                });
              }}
            >
              {i18n.t("Global.Buttons.Delete")}
            </BFButton>
            <FormFieldValues names={["invoiceKind", "invoiceId"]}>
              {([kind, invoiceId]) => (
                <BFButton
                  disabled={disableEdit}
                  appearance="outline"
                  onClick={() => {
                    openResubmissionDialog({
                      identifierToUpdate: RA_UPLOAD_INCOMING_INVOICE_LIST,
                      assetParams: {
                        assetType: AssetTypes.Invoicing.Invoice,
                        assetId: props.asset._id,
                        assetDescription: i18n.t(
                          "ra:UploadIncomingInvoice.Invoice",
                          "Rechnung"
                        ),
                      },
                      formTitle: i18n.t(
                        "ra:UploadIncomingInvoice.ResubmissionTitle",
                        "Zurückstellung der Rechnung"
                      ),
                      typePermissions: [props.asset.data.type],
                      initialValues: {
                        title: i18n.t(
                          "ra:UploadIncomingInvoice.ResubmissionTitle",
                          "Zurückgestellte {{typeOf}} - {{id}}",
                          {
                            typeOf: i18n.t(`Invoice.kinds.${kind}`, kind),
                            id: invoiceId || "###",
                          }
                        ),
                      },
                      onSubmit: async (resubmission) => {
                        await InvoiceService.deferInitInvoice(
                          props.asset._id,
                          resubmission.data.description
                        );

                        props.reloadList();
                      },
                      // typePermissions: props.typePermissions,
                    });
                  }}
                >
                  {i18n.t(
                    "ra:UploadIncomingInvoice.DeferInvoice",
                    "Zurückstellen"
                  )}
                </BFButton>
              )}
            </FormFieldValues>
            <div className="fill" />
            <BFButton
              disabled={disableEdit}
              appearance="primary"
              onClick={handleSubmit}
            >
              {i18n.t(
                "ra:UploadIncomingInvoice.SendToApproval",
                "Zur Genehmigung senden"
              )}
            </BFButton>
          </div>
        </div>
      )}
    />
  );
};
